// Region manager (Settings > Regions > Mesh regions, and RF Health's
// "+ Add region" menu): the admin API shapes and the pure logic behind the
// list editor and the custom-region polygon editor. NO imports on purpose --
// compiled on its own and covered by node:test
// (frontend/tests/regionManager.test.mjs).

export interface MeshRegionRow {
  code: string;
  name: string;
  display_name: string | null;
  source: string; // "meshmapper" | "custom"
  /** A MeshMapper region the key does not cover: kept as stored, never refreshed. */
  frozen?: boolean;
  enabled: boolean;
  state: string | null;
  greater_regions: string[];
  polygon: [number, number][];
}

export interface MeshRegionsResponse {
  regions: MeshRegionRow[];
  states: string[];
  greater_regions: string[];
}

export interface ImportCandidate {
  name: string;
  polygon: [number, number][];
  point_count: number;
}

export type LatLng = [number, number]; // [lat, lng] -- the API's own order

export const MIN_POLYGON_POINTS = 3;

export function sourceLabel(source: string, frozen = false): string {
  if (source === "custom") return "Custom";
  return frozen ? "MeshMapper (frozen, not covered by your key)" : "MeshMapper";
}

export function rowDisplayName(r: Pick<MeshRegionRow, "name" | "display_name">): string {
  const d = (r.display_name ?? "").trim();
  return d || r.name;
}

// --- list editor ---------------------------------------------------------------

export interface RegionDraft {
  enabled: boolean;
  displayName: string;
}

export function draftFromRow(r: MeshRegionRow): RegionDraft {
  return {
    enabled: r.enabled,
    displayName: r.display_name ?? "",
  };
}

export interface SettingsPatch {
  enabled?: boolean;
  display_name?: string | null;
}

/** PATCH /api/regions/mesh/{code}/settings body: only the fields that differ
 *  from the stored row. Empty text clears display_name (null). */
export function buildSettingsPatch(row: MeshRegionRow, draft: RegionDraft): SettingsPatch {
  const patch: SettingsPatch = {};
  if (draft.enabled !== row.enabled) patch.enabled = draft.enabled;
  const dn = draft.displayName.trim();
  if ((dn || null) !== ((row.display_name ?? "").trim() || null)) patch.display_name = dn || null;
  return patch;
}

export function isEmptyPatch(p: SettingsPatch): boolean {
  return Object.keys(p).length === 0;
}

/** API error body -> one readable string, verbatim where the API gave text. */
export function formatApiDetail(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (d && typeof d === "object" && "msg" in d) {
          const loc = Array.isArray((d as { loc?: unknown[] }).loc) ? ((d as { loc: unknown[] }).loc.slice(1).join(".")) : "";
          return loc ? `${loc}: ${String((d as { msg: unknown }).msg)}` : String((d as { msg: unknown }).msg);
        }
        return typeof d === "string" ? d : JSON.stringify(d);
      })
      .join("; ");
  }
  if (detail === null || detail === undefined) return "";
  return JSON.stringify(detail);
}

// --- custom region polygon editor ----------------------------------------------

export const CODE_PATTERN = /^[a-z0-9-]+$/;

export function isValidCode(code: string): boolean {
  return CODE_PATTERN.test(code);
}

export const MAX_CODE_LENGTH = 40;

/** Code typed by hand: lowercase, spaces become dashes, capped at 40. Nothing else is dropped while typing. */
export function normalizeCodeInput(raw: string): string {
  return raw.toLowerCase().replace(/\s/g, "-").slice(0, MAX_CODE_LENGTH);
}

/** A code made from a name: lowercase, spaces to dashes, other characters dropped, max 40. */
export function slugifyCode(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, MAX_CODE_LENGTH);
}

/** Picking an imported shape: fill Name from the shape's name and Code from a slug of it, only where still empty. */
export function fillFromCandidate(cur: { code: string; name: string }, c: ImportCandidate): { code: string; name: string } {
  const name = cur.name.trim() ? cur.name : c.name.trim();
  const code = cur.code.trim() ? cur.code : slugifyCode(name);
  return { code, name };
}

export function addPoint(points: LatLng[], p: LatLng): LatLng[] {
  return [...points, p];
}

export function undoPoint(points: LatLng[]): LatLng[] {
  return points.slice(0, -1);
}

export function clearPoints(): LatLng[] {
  return [];
}

export function canSavePolygon(points: LatLng[]): boolean {
  return points.length >= MIN_POLYGON_POINTS;
}

/** Can the custom form be saved? Needs a valid code (new regions only), a name, >= 3 points. */
export function canSaveCustom(opts: { code: string; name: string; points: LatLng[]; codeLocked: boolean }): boolean {
  return (opts.codeLocked || isValidCode(opts.code)) && opts.name.trim().length > 0 && canSavePolygon(opts.points);
}

/** Why the custom form cannot be saved yet (shown as the Save button's tooltip), or null when it can. */
export function saveBlockedReason(opts: { code: string; name: string; points: LatLng[]; codeLocked: boolean }): string | null {
  if (!opts.codeLocked && !isValidCode(opts.code)) {
    return opts.code.trim() === "" ? "Enter a code (lowercase letters, numbers and dashes)" : "The code may only use lowercase letters, numbers and dashes";
  }
  if (opts.name.trim().length === 0) return "Enter a name";
  if (!canSavePolygon(opts.points)) return `Add at least ${MIN_POLYGON_POINTS} points on the map (${opts.points.length} so far)`;
  return null;
}

/** GeoJSON [lng, lat] features for the live preview: the corner points, plus a
 *  line (2 points) or a closed polygon ring (3+). */
export function previewGeoJSON(points: LatLng[]): {
  points: [number, number][];
  line: [number, number][] | null;
  ring: [number, number][] | null;
} {
  const lngLat = points.map((p) => [p[1], p[0]] as [number, number]);
  return {
    points: lngLat,
    line: lngLat.length >= 2 ? lngLat : null,
    ring: lngLat.length >= 3 ? [...lngLat, lngLat[0]] : null,
  };
}

export function createPayload(code: string, name: string, points: LatLng[]): { code: string; name: string; polygon: LatLng[] } {
  return { code, name: name.trim(), polygon: points.map((p) => [p[0], p[1]] as LatLng) };
}

export function updatePayload(name: string, points: LatLng[]): { name: string; polygon: LatLng[] } {
  return { name: name.trim(), polygon: points.map((p) => [p[0], p[1]] as LatLng) };
}

/** Candidate picked from an import: its ring becomes the editable point list. */
export function pointsFromCandidate(c: ImportCandidate): LatLng[] {
  return c.polygon.map((p) => [p[0], p[1]] as LatLng);
}

export function candidateLabel(c: ImportCandidate): string {
  return `${c.name} (${c.point_count} points)`;
}
